import { AssetsProvider, getHomeUrl, RESOURCE_LINKS } from '@obvio/app'
import { resolveResourceLinks, serverClient } from '@obvio/server'
import { NextSeo } from 'next-seo'

import { schema } from '@/api/schema'
import { Layout } from '@/components/Layout'
import { NavFormEvent } from '@/components/NavForms/NavFormEvent'
import { Renderer } from '@/components/Renderer'
import { RESOURCE_PREFIXES } from '@/constants'
import { serverPrefetchQueries } from '@/constants/queries'

import type { LayoutType } from '@/components/Layout'
import type { RendererData } from '@obvio/layout/renderer'
import type { GetStaticPropsContext, GetStaticPropsResult } from 'next'
import type { ReactElement } from 'react'

function Page({
  page,
}: {
  page: {
    id: string
    slug: string
    data: RendererData<any, any>[]
    layoutRef: { title: LayoutType }
    seoTitle?: string
    seoDescription?: string
  }
}): ReactElement {
  const canonical = `${getHomeUrl()}/${page.slug ?? ''}`
  return (
    <>
      <NextSeo
        canonical={canonical}
        {...(page.seoTitle && { title: page.seoTitle })}
        {...(page.seoDescription && { description: page.seoDescription })}
      />
      <Layout type={page.layoutRef.title}>
        <AssetsProvider path={`/page/${page.id}`}>
          <Renderer data={page.data} />
          <NavFormEvent />
        </AssetsProvider>
      </Layout>
    </>
  )
}

export async function getStaticProps(ctx: GetStaticPropsContext): Promise<
  GetStaticPropsResult<{
    page: {
      id: string
      data: string
      layoutRef: { title: string }
    }
  }>
> {
  const client = await serverClient(schema, { ...ctx, isAdmin: true })

  const page = await client.query('getPage', {
    args: {
      slug: 'pokoje',
    },
    select: {
      slug: true,
      data: true,
      layoutRef: {
        title: true,
      },
      seoTitle: true,
      seoDescription: true,
    },
  })

  await serverPrefetchQueries(page?.data ?? [], client)

  if (!page) {
    return { notFound: true }
  }

  return {
    ...client.ssrData({
      page,
      [RESOURCE_LINKS]: await resolveResourceLinks(
        page.data,
        RESOURCE_PREFIXES,
      ),
    }),
  }
}

export default Page
